import { Cloud } from 'lucide-react';
import { useEffect, useState } from 'react';
import { DayForecast } from '../components/weather/DayForecast';
import { getForecast } from '../services/weatherApi';
import { Helmet } from 'react-helmet-async';

interface WeatherForecast {
  date: string;
  maxTemp: number;
  minTemp: number;
  condition: string;
  windSpeed: number;
  precipitation: number;
  icon: string;
}

export function Weather() {
  const [forecast, setForecast] = useState<WeatherForecast[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getForecast();
        
        const processedForecast = data.forecast.forecastday.map((day) => ({
          date: new Date(day.date).toLocaleDateString('no-NO', { 
            weekday: 'long', 
            day: 'numeric', 
            month: 'long' 
          }),
          maxTemp: Math.round(day.day.maxtemp_c),
          minTemp: Math.round(day.day.mintemp_c),
          condition: day.day.condition.text,
          windSpeed: Math.round(day.day.maxwind_kph / 3.6), // Convert to m/s
          precipitation: day.day.daily_chance_of_rain,
          icon: day.day.condition.icon
        }));

        setForecast(processedForecast);
      } catch (err) {
        setError('Could not load weather data. Please try again later.');
        console.error('Failed to fetch weather:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, []);

  return (
    <>
      <Helmet>
        <title>Værvarsel | Aker Stadion, Molde</title>
        <meta 
          name="description" 
          content="Detaljert værvarsel for Aker Stadion i Molde. Sjekk temperaturer, nedbør og vindforhold for å planlegge ditt stadionbesøk."
        />
      </Helmet>
      <div className="min-h-screen bg-gray-100">
        <div className="bg-[#2173B9] text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <Cloud className="w-8 h-8" />
              <h1 className="text-3xl font-bold">Weather Forecast for Aker Stadium</h1>
            </div>
            <p className="text-lg opacity-90">
              Get updated weather forecasts for the coming days
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Laster værdata...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {forecast.map((day, index) => (
                  <DayForecast key={index} {...day} />
                ))}
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-2xl font-bold mb-4">Weather Forecast for Molde</h2>
                <p className="text-gray-600 mb-4">
                  Get updated weather forecasts for Molde and Aker Stadium. Plan your visit to 
                  the stadium with our weather widget, which gives you the latest information about 
                  temperatures, precipitation, and wind conditions for the coming days.
                </p>
                <p className="text-gray-600">
                  Check the hour-by-hour weather forecast to ensure you're well prepared for a 
                  day at the stadium. See max/min temperatures, expected precipitation, and wind speed, 
                  so you can plan what to wear and how to get the best stadium experience.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
